import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Activity, Clock, Hash, User, X } from 'lucide-react';

import { cn } from '../lib/utils';
import { Badge } from '../primitives/badge';
import { Button } from '../primitives/button';

export interface AuditEvidenceRef {
  /** Kind of evidence: 'attachment' | 'evidence-pack' | 'signature' | 'scenario-export' | custom. */
  kind: string;
  /** Canonical id within the kind. */
  id: string;
  /** Optional human description for the drawer. */
  description?: string;
  /** Optional link the user can click. */
  url?: string;
}

/**
 * One entry in the audit trail. Mirrors the canonical
 * <c>Tyndo.Core.Audit.AuditEnvelope</c> wire shape plus the
 * legacy fields the older drawer call sites already pass. New call
 * sites should populate `role`, `previousState`, `nextState`,
 * `evidenceRefs[]`, and `idempotencyKey` from the backend
 * `/audit-chain` endpoint; older sites that pass only `transition` +
 * `evidenceRef` continue to render unchanged.
 */
export interface AuditEntry {
  /** Stable id for the entry, used as a list key + correlation reference. */
  id: string;
  /** Who took the action — human name or system label. */
  actor: string;
  /** UTC timestamp the action was recorded. Pass an ISO string or Date. */
  timestamp: string | Date;
  /** What changed — a verb (e.g. "Released", "Signed", "Rejected"). */
  transition: string;
  /** Free-text reason captured at action time. */
  reason?: string;
  /** Reference to attached evidence (signature id, blob id, file path). Legacy single-ref form. */
  evidenceRef?: string;
  /** End-to-end correlation id for cross-service trace lookup. */
  correlationId?: string;
  /** Optional context — entity state before, after, kind. */
  metadata?: Record<string, unknown>;

 // ─── envelope fields ──────────────────────────────────────
  /** Effective role / permission claim that authorized the action. */
  role?: string;
  /** Lifecycle state immediately before the transition. */
  previousState?: string;
  /** Lifecycle state immediately after the transition. */
  nextState?: string;
  /** Idempotency-Key from the originating request. */
  idempotencyKey?: string;
  /** Tenant scope (uuid). */
  tenantId?: string;
  /** Site scope (uuid) for multi-site deployments. */
  siteId?: string;
  /** Multi-evidence refs (preferred over `evidenceRef`). */
  evidenceRefs?: AuditEvidenceRef[];
  /** Simulator scenario id that drove this action, if any. */
  scenarioId?: string;
}

export interface AuditDrawerProps {
  /** Controls open state. Drives a Radix Dialog under the hood. */
  open: boolean;
  /** Called when the user dismisses the drawer (click X, Esc, click outside). */
  onOpenChange: (open: boolean) => void;
  /** Display title — usually "Audit trail" or the entity name + "audit". */
  title: string;
  /** Optional one-line subtitle. */
  subtitle?: string;
  /** Audit entries — usually loaded via useApiQuery; pass empty array for empty-state. */
  entries: AuditEntry[];
  /** When true, replace the entries list with a skeleton. */
  loading?: boolean;
}

const formatTimestamp = (t: string | Date): string => {
  const d = t instanceof Date ? t : new Date(t);
  if (Number.isNaN(d.getTime())) return String(t);
  return d.toISOString().replace('T', ' ').replace('Z', ' UTC');
};

/**
 * Shared regulated-record audit drawer. Slides in from the right.
 * Displays a chronological list of audit entries with actor + timestamp
 * + transition + reason + evidence + correlation id.
 *
 * Use on every regulated or financial view that mutates state:
 * PLM release, EBR sign, batch release, compliance approve,
 * inventory release, material lot release, finance post,
 * procurement approve, sales invoice, quality close.
 *
 * @example
 * const [auditOpen, setAuditOpen] = useState(false);
 * <Button variant="ghost" onClick={() => setAuditOpen(true)}>
 *   <History className="h-4 w-4" /> Audit
 * </Button>
 * <AuditDrawer
 *   open={auditOpen}
 *   onOpenChange={setAuditOpen}
 *   title="Audit trail"
 *   subtitle={`Batch record ${record.batchNumber}`}
 *   entries={auditQuery.data ?? []}
 *   loading={auditQuery.isLoading}
 * />
 */
export function AuditDrawer({
  open,
  onOpenChange,
  title,
  subtitle,
  entries,
  loading = false,
}: AuditDrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        <DialogPrimitive.Content
          data-slot="audit-drawer"
          className={cn(
            'fixed inset-y-0 right-0 z-50 flex h-full w-full max-w-xl flex-col',
            'border-l border-border/60 bg-surface-overlay shadow-[var(--shadow-overlay)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
            'duration-200',
          )}
        >
          <header className="flex items-start justify-between gap-3 border-b border-border/60 p-5">
            <div className="space-y-1">
              <DialogPrimitive.Title className="flex items-center gap-2 text-base font-semibold">
                <Activity className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                {title}
              </DialogPrimitive.Title>
              {subtitle && (
                <DialogPrimitive.Description className="text-sm text-muted-foreground">
                  {subtitle}
                </DialogPrimitive.Description>
              )}
            </div>
            <DialogPrimitive.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Close audit drawer">
                <X className="h-4 w-4" />
              </Button>
            </DialogPrimitive.Close>
          </header>

          <div className="flex-1 overflow-y-auto p-5">
            {loading ? (
              <div className="space-y-3" role="status" aria-label="Loading audit entries">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-16 rounded-md border border-border/40 bg-muted/30 animate-pulse"
                  />
                ))}
              </div>
            ) : entries.length === 0 ? (
              <div
                className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground"
                data-testid="audit-drawer-empty"
              >
                <Activity className="h-6 w-6 opacity-50" aria-hidden="true" />
                <p>No audit entries yet.</p>
                <p className="max-w-[28ch] text-center text-xs">
                  Audit rows are written when a regulated mutation succeeds.
                </p>
              </div>
            ) : (
              <ol className="space-y-3" data-testid="audit-drawer-entries">
                {entries.map((e) => (
                  <li
                    key={e.id}
                    className="rounded-md border border-border/60 bg-surface p-3"
                  >
                    <div className="mb-1 flex items-center justify-between gap-3">
                      <Badge variant="outline" className="font-mono text-xs">
                        {e.transition}
                      </Badge>
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        {formatTimestamp(e.timestamp)}
                      </span>
                    </div>
                    <p className="mb-1 flex items-center gap-1 text-sm font-medium">
                      <User className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                      {e.actor}
                      {e.role && (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          as <code className="font-mono">{e.role}</code>
                        </span>
                      )}
                    </p>
                    {(e.previousState || e.nextState) && (
                      <p className="mb-1 text-xs text-muted-foreground">
                        <code className="font-mono">{e.previousState ?? '∅'}</code>
                        {' → '}
                        <code className="font-mono">{e.nextState ?? '∅'}</code>
                      </p>
                    )}
                    {e.reason && (
                      <p className="mb-2 text-sm text-muted-foreground">{e.reason}</p>
                    )}
                    {(e.evidenceRef ||
                      (e.evidenceRefs && e.evidenceRefs.length > 0) ||
                      e.idempotencyKey ||
                      e.correlationId) && (
                      // Technical correlation refs (evidence hashes, idempotency
                      // key, trace id) are auditor-grade detail that clutters the
                      // operator's scan of the trail. Tuck them behind a native
                      // <details> disclosure — keyboard- and screen-reader-friendly
                      // out of the box, and find-in-page still reaches the hidden
                      // ids when an investigator searches for one.
                      <details className="group mt-1">
                        <summary className="inline-flex cursor-pointer list-none items-center gap-1 text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
                          <Hash className="h-3 w-3" aria-hidden="true" />
                          <span className="group-open:hidden">Technical references</span>
                          <span className="hidden group-open:inline">Hide technical references</span>
                        </summary>
                        <div className="mt-1.5 flex flex-wrap gap-3 text-xs text-muted-foreground">
                          {e.evidenceRef && (
                            <span className="inline-flex items-center gap-1">
                              <Hash className="h-3 w-3" aria-hidden="true" />
                              evidence: <code className="font-mono">{e.evidenceRef}</code>
                            </span>
                          )}
                          {e.evidenceRefs?.map((ref) => (
                            <span key={`${ref.kind}:${ref.id}`} className="inline-flex items-center gap-1">
                              <Hash className="h-3 w-3" aria-hidden="true" />
                              {ref.kind}:{' '}
                              {ref.url ? (
                                <a className="font-mono underline" href={ref.url}>{ref.id}</a>
                              ) : (
                                <code className="font-mono">{ref.id}</code>
                              )}
                              {ref.description && <span className="opacity-70">— {ref.description}</span>}
                            </span>
                          ))}
                          {e.idempotencyKey && (
                            <span className="inline-flex items-center gap-1">
                              <Hash className="h-3 w-3" aria-hidden="true" />
                              idem: <code className="font-mono">{e.idempotencyKey}</code>
                            </span>
                          )}
                          {e.correlationId && (
                            <span className="inline-flex items-center gap-1">
                              <Hash className="h-3 w-3" aria-hidden="true" />
                              trace: <code className="font-mono">{e.correlationId}</code>
                            </span>
                          )}
                        </div>
                      </details>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

AuditDrawer.displayName = 'AuditDrawer';
